<template>
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerVirtualKeyboardProvider>
      <DrawerViewport>
        <!-- Unmount the content owner after its exit animation so modal cleanup runs once. -->
        <Presence :present="props.forceMount || root.open.value">
          <DrawerContent
            data-slot="drawer-content"
            v-bind="{ ...$attrs, ...forwarded }"
            force-mount
            :class="
              cn(
                'tw:group/drawer-content tw:bg-background tw:fixed tw:z-50 tw:flex tw:h-auto tw:flex-col',
                'tw:transform-[translate(var(--drawer-swipe-movement-x,0px),var(--drawer-swipe-movement-y,0px))]',
                'tw:transition-transform tw:duration-500 tw:ease-[cubic-bezier(0.32,0.72,0,1)] tw:data-swiping:duration-0 tw:data-swiping:select-none',
                'tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out',
                'tw:data-[swipe-direction=up]:inset-x-0 tw:data-[swipe-direction=up]:top-0 tw:data-[swipe-direction=up]:mb-24 tw:data-[swipe-direction=up]:max-h-[80vh] tw:data-[swipe-direction=up]:rounded-b-lg tw:data-[swipe-direction=up]:data-[state=open]:slide-in-from-top tw:data-[swipe-direction=up]:data-[state=closed]:slide-out-to-top',
                'tw:data-[swipe-direction=down]:inset-x-0 tw:data-[swipe-direction=down]:bottom-0 tw:data-[swipe-direction=down]:mt-24 tw:data-[swipe-direction=down]:max-h-[80vh] tw:data-[swipe-direction=down]:rounded-t-lg tw:data-[swipe-direction=down]:data-[state=open]:slide-in-from-bottom tw:data-[swipe-direction=down]:data-[state=closed]:slide-out-to-bottom',
                'tw:data-[swipe-direction=right]:inset-y-0 tw:data-[swipe-direction=right]:right-0 tw:data-[swipe-direction=right]:w-3/4 tw:data-[swipe-direction=right]:sm:max-w-sm tw:data-[swipe-direction=right]:data-[state=open]:slide-in-from-right tw:data-[swipe-direction=right]:data-[state=closed]:slide-out-to-right',
                'tw:data-[swipe-direction=left]:inset-y-0 tw:data-[swipe-direction=left]:left-0 tw:data-[swipe-direction=left]:w-3/4 tw:data-[swipe-direction=left]:sm:max-w-sm tw:data-[swipe-direction=left]:data-[state=open]:slide-in-from-left tw:data-[swipe-direction=left]:data-[state=closed]:slide-out-to-left',
                props.class,
              )
            "
          >
            <DrawerHandle
              class="tw:bg-(--surface-handle) tw:mx-auto tw:mt-2.5 tw:mb-1.5 tw:hidden tw:h-1.25 tw:w-11 tw:shrink-0 tw:rounded-full tw:group-data-[swipe-direction=down]/drawer-content:block"
            />
            <slot />
          </DrawerContent>
        </Presence>
      </DrawerViewport>
    </DrawerVirtualKeyboardProvider>
  </DrawerPortal>
</template>

<script setup>
import { reactiveOmit } from '@vueuse/core';
import { cn } from '@/shared/utilities/classes';
import {
  DrawerContent,
  DrawerHandle,
  DrawerPortal,
  DrawerViewport,
  DrawerVirtualKeyboardProvider,
  Presence,
  injectDrawerRootContext,
  useForwardPropsEmits,
} from 'reka-ui';
import DrawerOverlay from './DrawerOverlay.vue';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  forceMount: { default: undefined, type: Boolean, required: false },
  initialFocus: { default: undefined, type: [Boolean, null], required: false, skipCheck: true },
  finalFocus: { default: undefined, type: [Boolean, null], required: false, skipCheck: true },
  disableOutsidePointerEvents: { default: undefined, type: Boolean, required: false },
  asChild: { default: undefined, type: Boolean, required: false },
  as: { default: undefined, type: null, required: false },
  class: {
    default: undefined,
    type: [Boolean, null, String, Object, Array],
    required: false,
    skipCheck: true,
  },
});
const emits = defineEmits([
  'escapeKeyDown',
  'pointerDownOutside',
  'focusOutside',
  'interactOutside',
  'openAutoFocus',
  'closeAutoFocus',
]);

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);
const root = injectDrawerRootContext();
</script>
